// 🎯 컷 길이 결정 SSOT(회장님 2026-07-26 — "음성이 10.3초인데 영상이 10초로 잘린다").
//   같은 결정을 두 곳이 다른 산식으로 하고 있었다: 렌더는 ceil(음성+0.5)로 올려 잡는데(voice-first),
//   생성 직전 동기화는 Math.round 라 10.3초 음성이 10초 영상이 됐다(내림=발화 잘림). 여기로 모아
//   재분기를 막는다(#673 교훈 — 결정론 산식은 SSOT+테스트 게이트).
//
//   규칙(회장님 2026-07-26, #1423 A안 2026-08-03):
//   ① 자동값은 음성보다 길게 — ceil(음성+여운 패드).
//   ② 노드 제어 패널에서 "직접 조절한" 값은 ceil(음성)까지 허용하고, 그 값 아래로는 깎지 않는다.
//      (자동 상속값까지 지키면 짧은 대사 컷이 불필요하게 길어져 생성비가 오르므로, 노드 생성 호출부는
//       사용자 명시 설정일 때만 plannedSec + mode="manual" 을 넘긴다.)

import { sedeoModelDef } from "./sedeo-models";
/* 🎬 옴니 계열 길이 상한 — 판정(`isOmniFamilyCut`)도 값(`OMNI_MAX_SEC`)도 순수부가 소유한다.
   ★여기서 `genMode === "omni"` 를 손으로 비교하거나 10 을 리터럴로 박으면 그 순간 두 번째 사본이
   되고, 세대가 하나 더 붙거나 벤더 상한이 바뀌는 날 이 파일만 조용히 낡는다.
   ★순환 없음★: `omni-cut-contract` 는 런타임 import 가 0 이다(타입 전용 인라인 import 뿐). */
import { OMNI_MAX_SEC, isOmniFamilyCut } from "./omni-duration-core";
/* ⏱ 클링 벤더 상한(15초)·술어 — 정본은 이 모듈 하나다(옴니와 같은 관용구 · 런타임 import 0). */
import { KLING_MAX_SEC, isKlingO3Cut } from "./kling-cut-contract";

export const CUT_MIN_SEC = 5;
export const CUT_MAX_SEC = 15;
export const CUT_MAX_SEC_FAST = 12; // seedance-2.0-fast — EF generate-sedeo-video maxDuration 미러
/**
 * 🚧 **API 하한**(시댄스가 받아주는 최소 length) — CUT_MIN_SEC(5, "음성이 잘리지 않는 권장 하한")과
 * ★다른 값이고 다른 뜻★이다. 디렉터 EF·채팅 어댑터에서 들어온 길이를 "받아들일 수 있는 범위"로 접을
 * 때 쓴다. 여기로 모으기 전엔 4가 EF·인스펙터·채팅 어댑터에 리터럴로 흩어져 있었다.
 * ★인스펙터 수동 입력의 하한은 더 이상 무조건 4가 아니다(#1331 · Codex 2R): 음성 동기화가 도는 컷은
 *   생성이 5로 밀어 올리므로 화면도 5를 내건다. 4가 살아 있는 건 **동기화가 안 도는 컷**뿐이다.
 *   판정은 cutDurationBounds 가 소유한다 — 화면이 4/5를 스스로 고르면 그 순간 다시 갈라진다.
 * ★권장 하한으로 올려 접지 않는 이유: 디렉터가 4초를 고른 건 그 컷이 짧아야 한다는 ★연출 판단★이고,
 *   5로 밀어 올리면 생성비만 오른다. 접는 것은 "API 가 거부하는 값"까지다.
 */
export const CUT_HARD_MIN_SEC = 4;
/** 음성 끝의 숨·여운 — 렌더 voice-first(fitDurationToVoice)와 동일 값. */
export const VOICE_TAIL_PAD_SEC = 0.5;

/** 모델별 컷 상한(EF 클램프와 같은 판정).
 *  ★종전 `fast ? 12 : 15` 삼항은 모델 2개까지만 표현할 수 있었다 — mini 를 그 불리언에 얹으면
 *   상한이 15초인데도 12초로 잘려 대사가 끊긴다. 판정을 카탈로그 조회로 옮긴다. */
export function cutMaxSecForModel(model?: string | null): number {
  return sedeoModelDef(model).maxDurationSec;
}

/**
 * 🎬 **🎬스토리줄기(story 다컷) 레인의 컷 상한 — 1차 계약 = 15초**(2026-08-12 T1-1).
 *
 * ★왜 모델 파생을 그대로 쓰지 않나★: story 는 다컷 체인이고, 디렉터(EF)는 이 상한을 **프롬프트
 *   계약**으로 받는다. 모델이 2.5 로 바뀌는 순간 프론트만 30 을 계산하면 ★디렉터는 15초 컷으로 콘티를
 *   짜는데 프론트는 30초로 굽는다★ — 콘티 카드와 실제 클립이 갈린다(설계서 §1-2 "15가 6곳, 30이 8곳").
 *   그래서 1차는 **양쪽 다 15 로 접는다**. 30 개방(긴 컷 재편 · 다컷 전반)은 M1 실측 게이트를 통과한 2차의 일이다.
 * ⏱ **예외 하나 — 원테이크 골격 컷**(2026-10-21 · 회장님 «2번으로 가» = 전문가 대담 30초 한 편 ≈$3.1):
 *   «비트 하나 · 반복 없음» 골격은 컷이 늘 1개라 영상 길이 = 그 컷 길이다. 디렉터는 이 골격에서 `genStyle.oneTakeMaxSec`
 *   (생성형 `genOneTakeMaxSec` · 목록 스타일만)을 받아 컷 하나를 그 길이까지 쓴다. 화면이 여기서 15로 접으면 견적은 15 · 음성
 *   동기화는 15로 잘라 «30초 한 편» 결정이 손님에게 안 닿는다. 그래서 `oneTakeMaxSec` 이 들어온 컷만 `min(oneTakeMaxSec, 모델 상한)`
 *   까지 연다 — ★값은 호출부가 콘티 도장(`__oneTakeMaxSec` = 그 콘티 요청에 ★실제로 실린★ payload 값 · `contiOneTakeMaxSec`)으로
 *   조달한다★(컷 1개 콘티에서만 · 컷을 늘리면 도장 무시 = 종전 15 · 10-22). 스타일 id 목록을 여기·화면에 따로 적지 않는다(생성형이 목록을 넓히면 payload 를 타고 자동으로 따라간다).
 *   ★도장이 없는 콘티 = 종전 15★ — 이 변경 전에 15초 컷으로 짠 콘티가 굽기 때 음성 길이로 30까지 늘어나 «견적 15 · 청구 30»이
 *   되지 않게, 상한은 «디렉터가 그 길이를 알고 짠 콘티»에서만 열린다. 미전달·1 미만·비수치 = 종전 15.
 *   견적·굽기·씬 다시 만들기가 전부 이 함수 하나를 읽어 콘티 카드와 실제 클립이 갈리지 않는다(디렉터 요청은 골격 동승 시
 *   `cutMaxSec` 을 지우고 `genStyle.oneTakeMaxSec` 이 상한을 정한다 — 모델로 깎은 같은 값 `directorOneTakeMaxSec` 이다).
 * ★`Math.min` 인 이유★: 상한을 **올리지 않는다**. fast(12s)처럼 모델 자체가 더 낮으면 그쪽이 이긴다 —
 *   그런 컷을 13~15s 로 잠그면 EF 가 다시 자른다(#1113 과 같은 축).
 * ★T3 이후 = **실제로 일하는 보호막**★(2026-08-13): story 기본이 `sedeo_25` 게이트 테넌트에서 2.5
 *   (상한 30)로 넘어갔다. 그 테넌트에서 이 함수는 `min(15, 30) = 15` 로 접으며, 게이트 OFF 테넌트는
 *   종전대로 `min(15, 15) = 15` — 즉 **두 테넌트가 같은 컷 상한을 본다**(콘티↔실클립 분열 0).
 * ★적용 범위★: story 레인 한정. ⚡한 방(oneshot·통짜 30초)·노드 캔버스·인스펙터는 **다른 축**이라
 *   `cutMaxSecForModel` 을 그대로 쓴다(그쪽에 이 함수를 끼우면 30초 원테이크가 15로 잘린다).
 *   ★단 ★씬 1개 재생성★ 은 story 레인이면 이 함수를 쓴다 — 제작이 15로 구운 슬롯을 재생성만 30으로
 *     요청하면 합본 슬롯이 어긋난다(T1-1 이 예고한 갈림 · T3 에서 확정).
 */
export const STORY_CUT_MAX_SEC = 15;
export function cutMaxSecForStory(model?: string | null, oneTakeMaxSec?: number | null): number {
  const oneTake = Number(oneTakeMaxSec);
  // ⏱ 원테이크 골격 컷만 그 상한까지(위 «예외 하나») — 모델 상한이 더 낮으면 모델이 이긴다(올리지 않는 극성 그대로).
  if (oneTakeMaxSec != null && Number.isFinite(oneTake) && oneTake >= 1) return Math.min(Math.floor(oneTake), cutMaxSecForModel(model));
  return Math.min(STORY_CUT_MAX_SEC, cutMaxSecForModel(model));
}

/**
 * ⏱ **콘티 도장 → 원테이크 한 컷 상한**(2026-10-21) — `cutMaxSecForStory` 둘째 인자의 유일한 조달구.
 *   바로제작 화면이 디렉터 요청 직후 `storyboard.__oneTakeMaxSec = directorOneTakeMaxSec(videoModel, 골격 값)` 으로 찍는다(디렉터에
 *   보낸 값과 같은 값 · 콘티는 작업물에 통째로 저장돼 복원돼도 같은 값). 도장 없음·1 미만·비수치 = null(종전 15).
 * ⏱ **★컷 1개 콘티에서만 유효★**(2026-10-22 · #6996 과금 리뷰 P2-1) — 도장은 «비트 하나 · 컷 하나» 골격의 약속이다.
 *   손님이나 디렉터가 도장 찍힌 콘티의 컷을 2개 이상으로 늘리면(`storyboard.cuts` — 컷 추가·나누기·다시 짜기가 다 이 배열을 고친다)
 *   도장을 무시하고 null(컷마다 종전 상한 `min(15, 모델 상한)`)을 돌려준다. 종전엔 늘린 컷마다 30초 견적이 붙고 굽기도 30초까지 열렸다.
 *   견적·굽기·씬 다시 만들기가 전부 이 함수 하나를 지나므로 셋이 같은 순간 같은 판정을 본다. 컷을 다시 1개로 줄이면 도장이 다시 산다.
 *   ★컷 배열 칸이 아예 없으면(`cuts` 미정의) 도장만 본다★ — 디렉터 응답 콘티는 늘 컷 배열을 들고 있어 화면에선 도달하지 않는 꼴이고,
 *   도장만 든 꼴의 종전 판독(#6996 시험)을 그대로 둔다. 배열이 아닌 값·빈 배열은 null.
 */
export function contiOneTakeMaxSec(storyboard: unknown): number | null {
  const sb = storyboard && typeof storyboard === "object" ? (storyboard as { __oneTakeMaxSec?: unknown; cuts?: unknown }) : undefined;
  const raw = sb?.__oneTakeMaxSec;
  const sec = typeof raw === "number" ? raw : NaN;
  if (!(Number.isFinite(sec) && sec >= 1)) return null;
  if (sb?.cuts !== undefined && !(Array.isArray(sb.cuts) && sb.cuts.length === 1)) return null; // ⏱ 컷 1개 콘티에서만(위 ★★)
  return Math.floor(sec);
}

/**
 * ⏱ **디렉터에 보낼 원테이크 한 컷 상한**(2026-10-22 · 본부 지시 #14) — 골격 값(`oneTakeMaxSec` · 전문가 대담 30)을 이 작품의
 *   영상 모델 상한으로 깎는다(2.5 → 30 · 2.0 → 15 · fast → 12). 디렉터(#6989)는 받은 값까지 컷을 열고 모델로는 깎지 않아서,
 *   깎지 않고 보내면 2.0·fast 작품이 30초 대사를 15·12초에 구워 말이 잘린다. 화면은 같은 값을 콘티 도장에 찍는다 →
 *   디렉터 = 도장 = 견적 = 굽기 = 다시 만들기(전부 `cutMaxSecForStory`).
 *   골격 값이 없거나 무효면 undefined — 키를 싣지 않는다(목록 밖 골격은 바이트 그대로).
 */
export function directorOneTakeMaxSec(model?: string | null, oneTakeMaxSec?: number | null): number | undefined {
  const sec = Number(oneTakeMaxSec);
  return oneTakeMaxSec != null && Number.isFinite(sec) && sec >= 1 ? cutMaxSecForStory(model, sec) : undefined;
}

/**
 * ⏱💳 **원테이크 컷의 견적 초 = 굽기 상한**(2026-10-22 · 본부 지시 #14 ① · 관제 위임 = 화면 표시만).
 *   굽기는 컷 길이를 굽는 순간 만든 음성 길이에 맞춘다(`resolveCutDuration` · 시댄스는 계획초를 안 쓴다 · #1423 A안).
 *   견적이 콘티 계획초를 세면 «계획초 < 음성»일 때 청구가 견적을 넘는다 — 원테이크는 그 폭이 상한(30)까지라 커진다.
 *   그래서 도장 있는 콘티의 컷만 견적에서 상한으로 센다 → 청구 초 ≤ 견적 초(실제 차감은 서버가 제출 초로 · 그대로).
 *   상한이 없으면(도장 없는 콘티) 같은 컷을 그대로 돌려준다 — 종전 견적 바이트 그대로.
 */
export function oneTakeQuoteCut<T extends object>(cut: T, oneTakeCapSec: number | null | undefined): T {
  const cap = Number(oneTakeCapSec);
  return oneTakeCapSec != null && Number.isFinite(cap) && cap >= 1 ? { ...cut, durationSec: Math.floor(cap) } : cut;
}

/** 🎬⤵️ 모델 강등으로 **길이를 접어야 하는 컷 하나**. */
export interface ModelDemotionRefitCut {
  id: string;
  /** 강등 전 화면 길이(초). */
  fromSec: number;
  /** 새 모델 상한으로 접은 길이(초). */
  toSec: number;
}

/** 🎬⤵️ 모델 강등 재적합 계획 — `cuts` 가 비면 재적합 불필요. */
export interface ModelDemotionRefitPlan {
  /** 새 모델의 컷 상한(초). */
  maxSec: number;
  /** 접어야 하는 컷들(상한 이하인 컷은 담기지 않는다). */
  cuts: readonly ModelDemotionRefitCut[];
}

/**
 * 🎬⤵️ **모델을 낮출 때 화면의 컷 길이를 그 모델 범위로 재적합**한다(2026-08-13 T3-B).
 *
 * ★고치는 결함★: 2.5(상한 30)에서 만든 16~30초 컷이 있는 채로 품질을 2.0(상한 15)으로 내리면,
 *   종전엔 **화면은 30을 그대로 들고 있고 제출 시점에 EF/리졸버가 조용히 15로 잘랐다**. 유저는
 *   30초 컷을 주문했다고 믿고 15초 클립을 받는다 — 고지 없는 결과 변경이자, 타임라인 슬롯 계산이
 *   화면 값을 믿는 자리에서는 합본 길이까지 어긋난다.
 * ★낮추기만 한다★: 해상도 강등(`clampResolutionToModel`)과 **같은 방향 계약**이다. 모델을 다시
 *   올려도 이 함수는 아무 컷도 돌려주지 않는다 — 상한을 올리는 순간 유저가 고른 적 없는 긴 컷이
 *   생기고 그게 곧 과금이다(길이 = 초당 과금 축).
 * ★판정은 화면 길이 하나★: 음성 하한·수동/자동 모드는 보지 않는다. 그 축들은 생성 직전
 *   `resolveCutDuration` 이 소유하며, 여기서 겹쳐 판정하면 두 벌 산식이 다시 갈린다.
 * ★`lockDuration` 컷도 접는다★: 잠금은 "자동 동기화가 건드리지 않는다"는 뜻이지 "모델 상한을
 *   넘겨도 된다"가 아니다. 안 접으면 그 컷만 화면 30 / 실제 15 로 남아 결함이 그대로 존치된다.
 * ★🎛 모션그래픽 컷은 제외한다★(2026-08-13 적대 R1 P1): M 컷은 **시댄스를 아예 타지 않고**
 *   Shotstack 범위(2~20초)를 쓴다. 시댄스 모델 상한으로 접으면 유저가 고른 적 없는 길이로
 *   ★없는 제약★ 을 걸어 16~20초 모션그래픽을 15로 잘라 버린다. 판정은 호출부가
 *   **`usesMotionGraphicDurationRange`**(길이 축 술어)로 내려 플래그로 넘긴다 — 이 순수 함수가
 *   노드 형태를 다시 해석하면 같은 판정이 두 벌이 된다(그 갈림이 #1331 계열 사고의 형태였다).
 *   ★★`isMotionGraphicCut`(생성 경로 축)을 쓰면 안 된다★★(적대 R2 H5): 그 술어의 `!videoUrl` 때문에
 *     **이미 렌더된 20초 M 컷**이 제외에서 빠져나와 15초로 접힌다 — 실물 산출을 잘라내는 오폭이다.
 */
export function planModelDemotionRefit(
  cuts: readonly { id: string; durationSec?: number | null; motionGraphic?: boolean }[],
  nextModel?: string | null,
): ModelDemotionRefitPlan {
  const maxSec = cutMaxSecForModel(nextModel);
  const out: ModelDemotionRefitCut[] = [];
  for (const c of cuts) {
    if (c.motionGraphic) continue; // 🎛 시댄스 상한의 적용 대상이 아니다(위 주석)
    const from = typeof c.durationSec === "number" && Number.isFinite(c.durationSec) ? c.durationSec : 0;
    if (from > maxSec) out.push({ id: c.id, fromSec: from, toSec: maxSec });
  }
  return { maxSec, cuts: out };
}

/** 🎛 모션그래픽(Shotstack) 범위 — supabase/functions/render-motion-graphic 의 클램프 미러(2~20초).
 *  시댄스 범위(4~15)와 ★다른 축★이다: M 컷은 시댄스를 아예 타지 않는다. */
export const MOTION_MIN_SEC = 2;
export const MOTION_MAX_SEC = 20;

/** 컷 길이 판정에 필요한 최소 형태 — SceneNode 타입 의존을 만들지 않으려 구조로 받는다. */
export interface CutDurationNodeLike {
  deviceCode?: string | null;
  videoUrl?: string | null;
  narration?: string | null;
  audioUrl?: string | null;
  lockDuration?: boolean;
}

/**
 * 🎛 이 컷이 **모션그래픽 렌더로 가는가**(M + 아직 영상 없음).
 * 이 경우 시댄스 범위·음성 길이 동기화가 전부 무관하다 — 생성 경로가 그 앞에서 갈라진다
 * (CinematicCanvas·inlineQuickProduce 둘 다 음성 동기화 **앞에서** 조기 반환).
 * ★M 이라도 videoUrl 이 있으면 재생성은 시댄스 경로다(범위도 시댄스 것).
 */
export function isMotionGraphicCut(node: CutDurationNodeLike): boolean {
  return node.deviceCode === "M" && !node.videoUrl;
}

/**
 * 🎛 **길이 축에서 이 컷이 모션그래픽 범위(2~20초)를 쓰는가** — 위 `isMotionGraphicCut` 과 ★다른 술어★다.
 *
 * ★왜 갈라야 하나★(2026-08-13 적대 R2 H5): 위 함수는 **생성 경로 갈림**("지금 무엇으로 만들 것인가")
 *   이라 `!videoUrl` 이 붙어 있다 — 이미 영상이 있으면 재생성은 시댄스 경로이기 때문이다. 그런데
 *   **길이 범위**는 그 축이 아니다: 이미 Shotstack 으로 렌더된 20초 M 컷은 videoUrl 이 있어도
 *   ★여전히 20초짜리 M 컷★ 이다. 여기에 `isMotionGraphicCut` 을 쓰면 그 컷이 술어를 통과해
 *   시댄스 상한(15초)으로 접힌다 — 유저가 만든 실물 산출을 없는 제약으로 잘라내는 것이다.
 * ★이웃 3곳과 같은 축★: 길이·상태 판정 표면들(`canvas-filmstrip-model` 등)은 전부 `deviceCode === "M"`
 *   단독으로 본다. 그 축에 맞춘다.
 */
export function usesMotionGraphicDurationRange(node: Pick<CutDurationNodeLike, "deviceCode">): boolean {
  return node.deviceCode === "M";
}

/**
 * 🎯 **생성 직전 음성 길이 동기화가 이 컷에 도는가** — 생성 경로 게이트의 SSOT(Codex 3R P2).
 * 종전엔 같은 조건이 CinematicCanvas(메인 생성)·inlineQuickProduce(바로제작)·InspectorPanel(화면
 * 하한)에 리터럴로 복제돼 있었고, 그래서 **모션그래픽 갈림이 한 곳에서만 누락**됐다(실제 사고).
 * 이 판정이 참이면 resolveCutDuration 이 CUT_MIN_SEC(5)을 하한으로 강제하고, 거짓이면 저장된
 * durationSec 이 그대로 EF 로 전송된다 — 화면 하한은 이 갈림을 그대로 따라야 거짓말을 안 한다.
 */
export function cutVoiceSynced(node: CutDurationNodeLike): boolean {
  // M(모션그래픽)은 동기화 블록 **앞에서** 반환된다 — 조건을 아무리 만족해도 동기화는 안 돈다.
  if (isMotionGraphicCut(node)) return false;
  // ★lockDuration(원샷·음성 확정) 컷은 건드리지 않는다.
  return !node.lockDuration && Boolean(node.narration?.trim() || node.audioUrl);
}

/** 자동 산정 시 음성이 잘리지 않는 권장 컷 길이 — 올림 + 여운 패드. 음성 없으면 0. */
export function voiceFloorSec(audioSec?: number | null, pad = VOICE_TAIL_PAD_SEC): number {
  if (!(typeof audioSec === "number") || !(audioSec > 0)) return 0;
  return Math.ceil(audioSec + pad);
}

export interface ResolveCutDurationInput {
  /** 측정된 음성 길이(초). 없으면 길이 결정에 관여하지 않는다. */
  audioSec?: number | null;
  /** 보존할 계획/슬롯 길이 — 이 값 아래로는 줄이지 않는다.
   *  노드 사용자 직접값이면 mode="manual"을 함께 전달하고, 콘티/상속 자동값은 넘기지 않는다. */
  plannedSec?: number | null;
  /** 수동 입력일 때만 명시 — 생략(자동/슬롯 보존)이면 현행 ceil(음성+0.5)을 유지한다. */
  mode?: "automatic" | "manual";
  minSec?: number;
  maxSec?: number;
  pad?: number;
}

export interface ResolvedCutDuration {
  /** EF 로 보낼 최종 컷 길이(정수 초, min~max 클램프). */
  durationSec: number;
  /** 적용된 음성 하한(자동=ceil(음성+0.5), 수동 mode=manual이면 ceil(음성), 0=음성 없음). */
  floorSec: number;
  /** ★음성이 모델 상한을 넘어 클램프됐다 = 이 컷은 발화가 잘린다(컷을 나눠야 함). */
  voiceExceedsCap: boolean;
  /** 최종 컷 끝과 음성 끝 사이 여운(초). 음성 없음이면 null, 상한 초과면 음수일 수 있다. */
  voiceTailSec: number | null;
  /** 수동 선택의 여운이 권장 0.5초보다 짧다 — UI가 "여운 없음"을 안내하는 신호. */
  hasShortVoiceTail: boolean;
}

/** 길이 입력창이 유저에게 **미리** 보여줘야 할 범위(#1331). */
export interface CutDurationBoundsInput {
  /** 이 컷에 구워진 음성 길이(초). 없으면 음성 하한 없음. */
  audioSec?: number | null;
  model?: string | null;
  pad?: number;
  /**
   * ★이 컷에 **생성 직전 음성 길이 동기화가 도는가**(Codex 2R P1).
   * 호출부는 생성 경로의 게이트를 그대로 옮겨 적어야 한다: `!lockDuration && (대본 있음 || 음성 있음)`.
   * 동기화가 돌면 resolveCutDuration 이 CUT_MIN_SEC(5)을 하한으로 강제하지만, **안 돌면** 저장된
   * durationSec 이 그대로 EF 로 전송된다 — 이때 4초는 진짜로 4초로 만들어진다. 이 갈림을 무시하고
   * 하한을 5로 고정하면 무대사 컷에서 "5초로 올라간다"는 **거짓 고지**가 되고 4초 선택권도 사라진다.
   */
  voiceSynced?: boolean;
  /**
   * 🎛 모션그래픽 렌더 경로인가(isMotionGraphicCut). 참이면 범위가 시댄스(4~15)가 아니라
   * Shotstack(2~20)이다 — 시댄스 범위를 내걸면 **없는 제약으로 2~3초 컷을 막는다**(Codex 3R P1).
   */
  motionGraphic?: boolean;
  /**
   * 🎬 **이 컷의 생성 방식**(`SceneNode.genMode` 원값) — 옴니 계열이면 상한이 벤더 상한 10초다(W1 2026-09-01).
   *
   * ★고치는 거짓 고지★: 종전 상한은 **시댄스 카탈로그 파생값**(12·15·30)뿐이라, 옴니 컷에서도
   *   입력창이 12·15 를 내걸었다. 그런데 제출부(`omniDurationSec`)가 그 값을 다시 10 으로 클램프한다 —
   *   유저는 12초를 고를 수 있었고, 화면·슬롯은 12를 믿는데 나오는 클립은 10초다. 고른 값과 쓰이는
   *   값이 갈리는 것은 이 함수가 처음부터 없애려던 결함(#1331) **그 자체**다.
   * ★불리언이 아니라 `genMode` 원값을 받는 이유★: 호출부가 술어를 스스로 부르게 하면 표면 수만큼
   *   그 호출이 복제되고, 한 곳만 빠뜨린 날 **그 화면만** 12초를 내건다(`cutVoiceSynced` 머리말이
   *   박제한 모션그래픽 누락 사고와 같은 형상). 판정은 이 함수 안에 한 벌만 둔다.
   * ★미전달 = 종전 동작 그대로★ — 시댄스·자동 컷의 상한은 1비트도 안 바뀐다.
   *
   * ⚠️ ★★남는 표면 4곳(회부 · 이 축을 아직 안 받는다)★★ — 좌표를 적어 두는 이유는 «어디까지 됐나»를
   *   다음 사람이 추정으로 메우지 않게 하기 위함이다:
   *     ① `NodeTimelinePane.boundsFor` — 타임라인 리사이즈가 종전 상한을 그대로 내건다.
   *     ② `cut-duration-from-timeline.planTimelineCutDuration` — 위 ①의 커밋 경로.
   *     ③ `agent/scene-spec-sheet-model.sceneSpecDurationBounds` — 익스퍼트 시트.
   *     ④ **`planModelDemotionRefit`** — 모델을 낮출 때 기존 컷을 새 상한으로 접는 재적합기인데,
   *        ★`genMode` 축에는 대응물이 없다★. 그래서 [생성 방식]을 옴니로 바꿔도 이미 12·15초로
   *        저장된 컷은 **자동으로 안 접힌다**(오늘은 인스펙터 고지 + [10초로 맞추기] 가 그 자리를
   *        사람 손으로 메운다 — 수리 R1 G5). 자동 재적합은 쓰기 경로 신설이라 별건이다.
   */
  genMode?: unknown;
}

export interface CutDurationBounds {
  /** 지금 이 컷에서 고를 수 있는 최소 초(상한으로 접힘). */
  minSec: number;
  /** 모델 상한. */
  maxSec: number;
  /** 음성이 요구하는 하한(0=음성 없음) — 안내 문구 노출 조건. */
  voiceFloorSec: number;
  /** ★음성이 상한을 넘는다 = 무슨 값을 골라도 발화가 잘린다(컷을 나눠야 함). */
  voiceExceedsCap: boolean;
}

/**
 * 🚧 **고를 수 있는 범위 = 실제로 쓰이는 범위**(#1331 · 2026-08-01).
 * 종전엔 인스펙터 입력창이 `min={4} max={15}` 리터럴을 박아 두고, 값이 실제로 유효한지는 생성 직전
 * resolveCutDuration 이 뒤늦게 판정했다. 그래서 당시 음성+0.5 하한이 6초인 컷에 유저가 5를 넣으면
 * **입력은 받아들여지고** 화면에 5가 남았다가 생성 순간 6으로 뒤집혔다(#1331 제보의 정체).
 * #1423 A안 이후에는 수동 물리 하한 ceil(음성)을 resolver에서 함께 계산해 유령 입력을 막으면서,
 * 자동 산식 ceil(음성+0.5)은 그대로 둔다.
 * ★상한 충돌: 음성이 모델 상한보다 길면 하한을 상한으로 접는다(min>max 인 입력창은 아무 값도 유효하지
 *   않아 그 자체로 막다른 길이다). 대신 voiceExceedsCap 으로 "컷을 나눠라"를 호출부가 말하게 한다.
 * ★바닥은 **동기화가 도는 컷에서만** CUT_MIN_SEC(5)다(Codex 1R P1 → 2R P1로 정정).
 *   - 동기화가 도는 컷: resolveCutDuration 의 minSec 기본값이 5라 4를 고르면 생성 때 5로 올라간다.
 *     그래서 4를 내걸면 **음성이 아주 짧거나 대본만 있는 컷에서도** 유령 입력이 남는다(실측: audioSec
 *     1·3·3.4 전부 UI 하한 4 → 실제 5).
 *   - 동기화가 **안 도는** 컷(무대사·무음 또는 lockDuration): 저장값이 그대로 EF 로 간다. EF 하한도 4라
 *     4초가 진짜로 4초로 만들어진다 — 여기서 5를 내걸면 없는 제약을 지어내는 것이다.
 */
export function cutDurationBounds(input: CutDurationBoundsInput = {}): CutDurationBounds {
  // 🎛 모션그래픽은 시댄스를 아예 안 탄다 — 범위도 음성 하한도 그쪽 계약이 아니다.
  // ★옴니 축보다 먼저 보는 것이 계약★: M 컷은 Shotstack 렌더라 옴니 EF 에 제출되지 않는다(생성
  //   경로가 그 앞에서 갈라진다). 옴니 상한을 M 컷에 씌우면 16~20초 모션그래픽을 10초로 자른다.
  if (input.motionGraphic) {
    return { minSec: MOTION_MIN_SEC, maxSec: MOTION_MAX_SEC, voiceFloorSec: 0, voiceExceedsCap: false };
  }
  const modelMaxSec = cutMaxSecForModel(input.model);
  /**
   * 🎬 **옴니 계열은 벤더 상한(10초)이 이긴다** — 시댄스 카탈로그 파생값이 아니다.
   * ★`Math.min` 이다 — 올리지 않는다★: 모델 상한이 이미 더 낮으면(fast 12·story 레인 등) 그쪽이
   *   이긴다. 상한을 넓히는 방향으로 쓰면 유저가 고른 적 없는 긴 컷이 생기고 그게 곧 과금이다
   *   (`cutMaxSecForStory`·`laneCutMaxSec` 과 같은 극성).
   * ★하한은 안 건드린다★: 옴니 하한(`OMNI_MIN_SEC` 3)은 여기 하한(4·5)보다 **낮아서**, 화면이
   *   4초를 내걸어도 제출부가 그 값을 그대로 싣는다(거짓말이 없다). 하한을 3으로 내리는 것은
   *   "고를 수 있는 값을 넓히는" 별개의 판단이라 이 수리의 범위가 아니다.
   */
  /* ⏱ [P1 · 과금 리뷰 2026-09-10] 클링(15초)도 벤더 상한을 가진다 — 빠지면 이 산식이 레인 상한을
     내주고 제출부가 조용히 접어 「같은 컷에 길이 둘」이 된다(옴니가 이 자리에 선 것과 같은 근거). */
  const maxSec = isOmniFamilyCut(input.genMode) ? Math.min(OMNI_MAX_SEC, modelMaxSec)
    : isKlingO3Cut(input.genMode) ? Math.min(KLING_MAX_SEC, modelMaxSec)
    : modelMaxSec;
  // 음성 동기화가 도는 컷에서만 생성 산식의 기본 하한(5)이 실제로 강제된다.
  const base = input.voiceSynced ? CUT_MIN_SEC : CUT_HARD_MIN_SEC;
  // ★수동 입력 범위도 resolveCutDuration 에 plannedSec 을 넘겨 같은 물리 하한을 읽는다(#1423).
  // UI가 Math.ceil(audioSec)을 따로 계산하면 다시 두 판정이 갈라지므로 resolver 결과만 소비한다.
  const resolved = resolveCutDuration({
    audioSec: input.audioSec,
    plannedSec: base,
    mode: "manual",
    minSec: base,
    maxSec,
    pad: input.pad,
  });
  return {
    minSec: resolved.durationSec,
    maxSec,
    voiceFloorSec: resolved.floorSec,
    voiceExceedsCap: resolved.voiceExceedsCap,
  };
}

/**
 * 💳 시댄스 실측 길이 → 요청 초 복원(벤더 프레임 보너스 제거 · 선재 결함 2026-08-13).
 *
 * 시댄스는 요청 N초에 **24N+1 프레임 = N + 1/24 ≈ N.042초**를 돌려준다(정본:
 * supabase/functions/generate-sedeo-video/cost-policy.ts §17.1 `sedeoOutputFrames`). 그래서 실측
 * videoDurationSec(parseMp4DurationSec)은 정상 경로에서 **항상 N.04** 고, 영상이 이긴 슬롯
 * (cinematicRender 길이 진실화의 base 낙착 → slotSec 2자리 반올림)도 같은 꼬리를 문다. 이 값을
 * resolveCutDuration 의 plannedSec 으로 그대로 넣으면 정수 올림이 벤더 보너스 프레임을
 * **유료 +1초**로 부풀린다(480p 재생성마다 +450 크레딧) — ceil 은 계약이므로(내림 = 발화 잘림)
 * resolver 가 아니라 실측이 planned 로 들어가는 **입구**에서 꼬리를 벗긴다.
 *
 * ★허용 오차 = 1.5프레임(1.5/24 = 0.0625초): 계약 꼬리 1/24(≈0.0417)가 mp4 timescale·2자리
 *   반올림을 거치며 0.04~0.05 로 흔들리는 폭을 덮는 값이다.
 * ★음성이 이긴 슬롯(voiceSec+패드)의 소수부는 임의값이라(opus 2R A-1 — "cue 끝 최소 0.1 단위"는
 *   slotSec 의 실제 생산자와 무관한 틀린 전제였다) 이 창 안에 확률적으로 들어올 수 있다. 그 구별은
 *   값 하나로는 불가능하므로 regenPlannedSlotSec 의 초과분 판별이 맡고, 오벗김의 손실 상한은
 *   정의상 이 창(0.0625초 슬롯 단축)이다 — 발화는 안 잘린다: TTS 연장(패드 0.35)은 초과분이
 *   정의상 패드보다 커서 애초에 벗겨지지 않고, 벗겨질 수 있는 패드 0(클립 원음) 연장은 재생성이
 *   발화 자체를 새로 굽는다(옛 파형 기준 없음 · opus 2R P3-3 — voiceFloor 는 복원 컷에서 안 도는
 *   축이라 근거로 쓰지 않는다).
 * ★프로바이더 무관 적용 — 비-sedeo 클립(kling i2v 등)은 요청이 정수라 no-op 이거나, 실측 꼬리가
 *   창 안일 때 같은 상한(≤1.5프레임)의 손실뿐이다(opus 2R A-5 박제).
 * ★정수·0 이하·NaN 은 그대로 통과 — 판정 없는 입력을 지어내지 않는다.
 */
export function stripSedeoFrameBonusSec(sec: number): number {
  if (!Number.isFinite(sec) || sec <= 0) return sec;
  const whole = Math.floor(sec);
  const frac = sec - whole;
  // +1e-9 = 경계 부동소수 방어(terra 2R P2) — `N + 1.5/24` 를 이진 부동소수로 재구성하면 frac 이
  // 1.5/24 보다 미세하게 커져 허용 창 밖으로 빠질 수 있다. 판정 의미는 "1.5프레임 이하" 그대로.
  return frac > 0 && frac <= 1.5 / 24 + 1e-9 ? whole : sec;
}

/**
 * 💳 씬 재생성 계획 초 입구 — 영속 슬롯 사슬(slotSec → videoDurationSec → 정수 설정 폴백)에서
 * plannedSec 을 뽑되, **벤더 프레임 보너스 꼬리만** 벗긴다(선재 결함 2026-08-13 · terra 2R P1
 * → opus 2R B-1 로 판별축 재설계).
 *
 * ★판별축 = "실측 영상 대비 초과분이 벗김 창(1.5/24초)보다 큰가"★:
 *   · 초과분 > 창 → 이 슬롯은 음성/cue 연장이 확실하다(예 10.05 = cue 끝 9.7 + 패드 0.35,
 *     영상 9.04) → 소수 **보존**, 종전대로 올림이 덮는다(내림 = 커버 손실). 복원 컷은
 *     audioDurationSec 을 일부러 안 실으므로(producedCuts 투영 주석) resolver 음성 하한이
 *     못 지켜 주는 자리 — 이 판별이 그 빈자리를 막는다.
 *   · 초과분 ≤ 창(영상 부재·slotSec≈실측 포함) → 벗긴다. ★"slotSec > 실측이면 연장" 단순 비교를
 *     쓰지 않는 이유(opus 2R B-1)★: videoDurationSec 은 계획값·재인코딩 값으로 덮이는 경로가
 *     실재해(renderVerify 구움·append-video-scene 등) 정수로 덮인 컷에서 20.04 > 20 이 참이 되어
 *     벤더 꼬리가 연장으로 오인 → 이 수리가 닫으려는 +1초 누수가 되살아난다. 초과분이 창 이하면
 *     벗겨도 손실이 정의상 창 이하(≤0.0625초 슬롯 단축)라 과금 안전 쪽으로 접는 것이 맞다.
 * ★"프리즈"가 아니라 "단축"이다(opus 2R B-4 정정)★ — 재생성 성공 시 소비처가 slotSec 을 지우고
 *   (CinematicOutputStep swap) 새 클립 실측에서 재파생하므로 클립<슬롯 프리즈는 안 생긴다.
 * ★수렴 한계(알려진 것)★: 수리 전에 이미 +1초로 부풀어 영속된 슬롯(예 21.04)은 21로 정수화될 뿐
 *   원래 요청(20)으로는 못 돌아간다 — 원 요청 초의 provenance 가 영속돼 있지 않아 "원래 21초 컷"과
 *   구별 불가. 이 함수의 계약은 "더 부풀지 않는다"까지다(복구는 provenance 신설이 필요한 별건).
 *   ★"정수 설정 = floor(슬롯)−1" 서명 복구(terra 2R 제안)는 기각★ — 바로제작 음성 동기화가
 *   올린 길이를 노드에 되쓰지 않아(inlineQuickProduce `sceneForGen` 사본 요청) 누수와 ★같은
 *   서명★을 남기는 정상 인구(음성이 설정을 딱 1초 올린 컷)가 실재하고, 복구하면 그 컷이 음성
 *   커버보다 1초 부족하게 요청된다(끝 프리즈) — 과금 절약보다 나쁜 방향의 오판이다.
 * ★같은 변환의 딴 창구★: 매니페스트 없는 구 작업물의 세그먼트 폴백(cinematicToStory
 *   sceneRegenCutFromSegments)은 Math.round(실측)라 벤더 꼬리(0.04)가 이미 떨어진다 — 누수는
 *   없고, 소수부 ≥0.5 내림은 그 레인의 선재 동작이다(통일은 별건 · opus 2R A-6).
 */
export interface RegenPlannedSlotInput {
  /** 렌더 확정 슬롯(cinematicRender 영속, 2자리 반올림) — max(실측 영상, 음성 끝+패드). */
  slotSec?: number | null;
  /** 실측 클립 길이(parseMp4DurationSec → videoDurationSec). ★계획값으로 덮였을 수 있다★. */
  videoDurationSec?: number | null;
  /** 정수 설정 폴백(재생성 매니페스트/노드 durationSec) — 꼬리가 없어 벗김이 no-op 이다. */
  fallbackSec?: number | null;
}
export function regenPlannedSlotSec(input: RegenPlannedSlotInput): number {
  // 원 사슬(slotSec ?? videoDurationSec ?? fallback, Number(…)||0)의 낙착 의미를 그대로 보존한다.
  // Number() 는 형 좁힘 뒤에도 남긴다 — 구 스냅샷의 문자열·쓰레기 값 방어(런타임 유일 방어선).
  const picked = input.slotSec ?? input.videoDurationSec ?? input.fallbackSec;
  const sec = Number(picked) || 0;
  if (sec <= 0) return sec;
  const video = Number(input.videoDurationSec);
  const voiceExtended = input.slotSec != null
    && Number.isFinite(video) && video > 0
    && sec - video > 1.5 / 24 + 1e-9; // 초과분 > 벗김 창 — 창 이하 초과는 연장이라도 벗김(손실 ≤ 창)
  return voiceExtended ? sec : stripSedeoFrameBonusSec(sec);
}

export function resolveCutDuration(input: ResolveCutDurationInput = {}): ResolvedCutDuration {
  const minSec = input.minSec ?? CUT_MIN_SEC;
  const maxSec = input.maxSec ?? CUT_MAX_SEC;
  const hasVoice = typeof input.audioSec === "number" && input.audioSec > 0;
  const hasPlannedDuration = typeof input.plannedSec === "number" && input.plannedSec > 0;
  const planned = hasPlannedDuration ? input.plannedSec! : 0;
  const isManual = input.mode === "manual";
  // #1423 A안: 실제 사용자 입력 호출만 mode=manual 을 명시해 물리 하한 ceil(음성)을 쓴다.
  // plannedSec 은 출력 재생성의 자동 슬롯 보존에도 쓰이므로 그 존재만으로 수동 판정하면 안 된다.
  const floorSec = voiceFloorSec(input.audioSec, isManual ? 0 : input.pad);
  // 정수 초로 올림 — 시댄스 length 는 정수 계약이고, 내림은 곧 발화 잘림이다.
  const target = Math.ceil(Math.max(floorSec, planned, minSec));
  const durationSec = Math.min(maxSec, Math.max(minSec, target));
  const voiceExceedsCap = floorSec > maxSec;
  const voiceTailSec = hasVoice ? durationSec - input.audioSec! : null;
  return {
    durationSec,
    floorSec,
    voiceExceedsCap,
    voiceTailSec,
    hasShortVoiceTail: isManual
      && !voiceExceedsCap
      && voiceTailSec !== null
      && voiceTailSec < VOICE_TAIL_PAD_SEC,
  };
}
